import api from './api';

const BASE = '/inventory';

const inventoryService = {

  // ── Overview Stats ──────────────────────────────────────────────────────────
  getStats: async () => {
    const { data } = await api.get(`${BASE}/stats`);
    return data;
  },

  // ── Items ───────────────────────────────────────────────────────────────────
  getItems: async ({ search, category, stockStatus } = {}) => {
    const params = {};
    if (search)      params.search      = search;
    if (category)    params.category    = category;
    if (stockStatus) params.stockStatus = stockStatus;
    const { data } = await api.get(`${BASE}/items`, { params });
    return data;
  },

  getItemByCode: async (code) => {
    const { data } = await api.get(`${BASE}/items/${code}`);
    return data;
  },

  addItem: async (payload) => {
    const { data } = await api.post(`${BASE}/items`, payload);
    return data;
  },

  updateItem: async (id, payload) => {
    const { data } = await api.put(`${BASE}/items/${id}`, payload);
    return data;
  },

  deleteItem: async (id) => {
    const { data } = await api.delete(`${BASE}/items/${id}`);
    return data;
  },

  getLowStockAlerts: async () => {
    const { data } = await api.get(`${BASE}/items/alerts/low-stock`);
    return data;
  },

  // ── Suppliers ───────────────────────────────────────────────────────────────
  getSuppliers: async () => {
    const { data } = await api.get(`${BASE}/suppliers`);
    return data;
  },

  addSupplier: async (payload) => {
    const { data } = await api.post(`${BASE}/suppliers`, payload);
    return data;
  },

  // ── Stock In ────────────────────────────────────────────────────────────────
  getStockIn: async () => {
    const { data } = await api.get(`${BASE}/stock-in`);
    return data;
  },

  getStockInById: async (id) => {
    const { data } = await api.get(`${BASE}/stock-in/${id}`);
    return data;
  },

  /**
   * @param {Object} payload
   * @param {string} payload.itemCode
   * @param {number} payload.quantity
   * @param {number} [payload.unitPrice]
   * @param {string} [payload.supplierCode]   - existing supplier code
   * @param {string} [payload.supplierNameManual] - new supplier name
   * @param {string} [payload.supplierContact]
   * @param {string} [payload.supplierPhone]
   * @param {string} [payload.invoiceNumber]
   * @param {string} payload.receivedDate   - yyyy-MM-dd
   * @param {string} [payload.receivedBy]
   * @param {string} [payload.remarks]
   */
  recordStockIn: async (payload) => {
    const { data } = await api.post(`${BASE}/stock-in`, payload);
    return data;
  },

  // ── Stock Out ───────────────────────────────────────────────────────────────
  getStockOut: async () => {
    const { data } = await api.get(`${BASE}/stock-out`);
    return data;
  },

  getStockOutById: async (id) => {
    const { data } = await api.get(`${BASE}/stock-out/${id}`);
    return data;
  },

  /**
   * @param {Object} payload
   * @param {string} payload.itemCode
   * @param {number} payload.quantity
   * @param {string} payload.issuedTo
   * @param {string} payload.purpose
   * @param {string} payload.issueDate      - yyyy-MM-dd
   * @param {string} [payload.authorizedBy]
   * @param {string} [payload.requisitionId]
   * @param {string} [payload.remarks]
   */
  recordStockOut: async (payload) => {
    const { data } = await api.post(`${BASE}/stock-out`, payload);
    return data;
  },

  // ── Purchase Requisitions ───────────────────────────────────────────────────
  getPurchaseReqs: async () => {
    const { data } = await api.get(`${BASE}/purchase-requisitions`);
    return data;
  },

  getPurchaseReqById: async (id) => {
    const { data } = await api.get(`${BASE}/purchase-requisitions/${id}`);
    return data;
  },

  createPurchaseReq: async (payload) => {
    const { data } = await api.post(`${BASE}/purchase-requisitions`, payload);
    return data;
  },

  /**
   * @param {number} id
   * @param {'Approved'|'Rejected'} action
   * @param {string} [approvedBy]
   * @param {string} [comments]
   */
  actionPurchaseReq: async (id, action, approvedBy = 'Admin', comments = '') => {
    const { data } = await api.patch(`${BASE}/purchase-requisitions/${id}/action`, {
      action, approvedBy, comments,
    });
    return data;
  },

  // Close a PR after stock received (PR is already Approved)
  closePurchaseReq: async (id) => {
    const { data } = await api.patch(`${BASE}/purchase-requisitions/${id}/action`, {
      action: 'Closed',
      approvedBy: 'System',
      comments: 'Stock received — PR closed automatically',
    });
    return data;
  },

  // ── Consumption Requisitions ────────────────────────────────────────────────
  getConsumptionReqs: async () => {
    const { data } = await api.get(`${BASE}/consumption-requisitions`);
    return data;
  },

  createConsumptionReq: async (payload) => {
    const { data } = await api.post(`${BASE}/consumption-requisitions`, payload);
    return data;
  },

  actionConsumptionReq: async (id, action, approvedBy = 'Admin', comments = '') => {
    const { data } = await api.patch(`${BASE}/consumption-requisitions/${id}/action`, {
      action, approvedBy, comments,
    });
    return data;
  },
};

export default inventoryService;